import { AggregatedStats, MatchPhone } from "refsix-js-models";
import { reduceMatchStats } from "refsix-core";

import { checkItemInFixture, getVenues } from "../../services/matchService";
import { normaliseName } from "../../utils/arrayUtils";
import { routes } from "../../route/constants";

/**
 * Pure helpers behind the SCORZA-739 venue page.
 *
 * The personal half of the page ("your history at this ground") is computed entirely from the
 * matches already in redux — no backend call — which is what gives the page value while the
 * community boards are still sparse. Everything here takes data and returns data so it can be
 * unit tested without a store or a network.
 */

/**
 * Mirrors `NULL_ISLAND_DEG` in refauth's `features/venues/VenueConfig.ts`. `Stats.gpsCenterPoint`
 * defaults to `{time: 0, latitude: 0, longitude: 0}`, and the usual `Object.keys(...).length > 0`
 * idiom does not catch that, so every consumer has to null-island check explicitly.
 */
export const NULL_ISLAND_DEG = 0.5;

/** Mirrors `BOARD_WINDOW_DAYS`; only a fallback — prefer the board payload's own `windowDays`. */
export const VENUE_WINDOW_DAYS_DEFAULT = 90;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export interface CentrePoint {
    latitude?: number;
    longitude?: number;
}

/** True when a GPS centre point is plausible enough to resolve a venue from. */
export function isUsableCentrePoint(point?: CentrePoint | null): boolean {
    if (!point) {
        return false;
    }
    const { latitude, longitude } = point;
    if (typeof latitude !== "number" || typeof longitude !== "number") {
        return false;
    }
    if (!isFinite(latitude) || !isFinite(longitude)) {
        return false;
    }
    if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) {
        return false;
    }
    return (
        Math.abs(latitude) >= NULL_ISLAND_DEG ||
        Math.abs(longitude) >= NULL_ISLAND_DEG
    );
}

/**
 * Whether the match summary should offer "View venue records". A venue can only be resolved for a
 * finished, tracked match that actually recorded where it was played.
 */
export function canViewVenueRecords(match?: MatchPhone): boolean {
    return !!(
        match &&
        match.matchFinished &&
        !match.matchAbandoned &&
        match.hasTracking &&
        isUsableCentrePoint(match.stats?.gpsCenterPoint)
    );
}

/**
 * The venue page URL for a match. The centre point resolves the community half via
 * `GET /venues/near`; the free-text venue name is what the personal half filters local matches
 * on, so both travel in the query string.
 */
export function venueRecordsRoute(match: MatchPhone): string {
    const centre = match.stats?.gpsCenterPoint;
    const params = new URLSearchParams();
    if (centre) {
        params.set("lat", String(centre.latitude));
        params.set("lon", String(centre.longitude));
    }
    if (match.venue) {
        params.set("venueName", match.venue);
    }
    if (match._id) {
        params.set("matchId", match._id);
    }
    return `${routes.venue}?${params.toString()}`;
}

/**
 * The venue strings actually present on the user's own matches that name the same ground. Users
 * type the venue by hand, so "Hackney Marshes" and "hackney  marshes" are the same place.
 */
export function venueAliasesInMatches(
    matches: MatchPhone[],
    venueName: string
): string[] {
    const target = normaliseName(venueName);
    if (!target) {
        return [];
    }
    return getVenues(matches).filter(
        (venue) => !!venue && normaliseName(venue) === target
    );
}

/** `Stats.sprintsTotal` is an array of per-category counts, not a scalar. Tolerate both. */
export function sumSprints(sprints?: number[] | number | null): number {
    if (typeof sprints === "number") {
        return sprints;
    }
    if (!Array.isArray(sprints)) {
        return 0;
    }
    return sprints.reduce((total, value) => total + (value || 0), 0);
}

export interface VenuePersonalHistory {
    /** The venue spellings on the user's own matches that were treated as this ground. */
    venueNames: string[];
    matches: MatchPhone[];
    matchCount: number;
    stats: AggregatedStats;
    /** Personal bests at this ground, in match units (m/s, metres, sprint count). */
    bestSpeedMax: number;
    bestDistanceTotal: number;
    bestSprintsTotal: number;
    firstPlayedAt: number | null;
    lastPlayedAt: number | null;
    /** The rolling window the community board uses, so both halves read on the same timescale. */
    windowDays: number;
    windowMatchCount: number;
    windowDistanceAverage: number;
}

/** A match's epoch, or null when it carries no parseable date. */
export function matchEpoch(match: MatchPhone): number | null {
    if (!match.date) {
        return null;
    }
    const epoch = new Date(match.date).getTime();
    return isNaN(epoch) ? null : epoch;
}

/**
 * "Your history at this ground", computed locally.
 *
 * Uses the same filter as `getFilteredStats` (finished, not abandoned) so the numbers agree with
 * the stats the user already sees elsewhere, then aggregates with `reduceMatchStats`.
 *
 * Note `reduceMatchStats` deliberately does not aggregate `speedMax` (a max is not a sum), so the
 * personal bests are derived here from the per-match stats.
 */
export interface VenueHistoryOptions {
    windowDays?: number;
    now?: number;
    /**
     * Drop this match from the history.
     *
     * The ribbon resolver needs the ground's state *before* the match just played, so that
     * beating your own previous best reads as "new personal best" rather than "you equalled
     * yourself". Every other caller wants the full history and omits this.
     */
    excludeMatchId?: string;
}

export function computeVenueHistory(
    allMatches: MatchPhone[] | undefined,
    venueName: string | null | undefined,
    windowDays: number = VENUE_WINDOW_DAYS_DEFAULT,
    now: number = Date.now(),
    options: VenueHistoryOptions = {}
): VenuePersonalHistory {
    const matches = allMatches || [];
    const venueNames = venueName
        ? venueAliasesInMatches(matches, venueName)
        : [];
    const excludeMatchId = options.excludeMatchId;

    const played = venueNames.length
        ? matches.filter(
              (match) =>
                  match.matchFinished &&
                  !match.matchAbandoned &&
                  !match.isTraining &&
                  (!excludeMatchId || match._id !== excludeMatchId) &&
                  checkItemInFixture(match, venueNames, "venue")
          )
        : [];

    const stats = reduceMatchStats(played);

    let bestSpeedMax = 0;
    let bestDistanceTotal = 0;
    let bestSprintsTotal = 0;
    let firstPlayedAt: number | null = null;
    let lastPlayedAt: number | null = null;

    played.forEach((match) => {
        bestSpeedMax = Math.max(bestSpeedMax, match.stats?.speedMax || 0);
        bestDistanceTotal = Math.max(
            bestDistanceTotal,
            match.stats?.distanceTotal || 0
        );
        bestSprintsTotal = Math.max(
            bestSprintsTotal,
            sumSprints(match.stats?.sprintsTotal)
        );

        const epoch = matchEpoch(match);
        if (epoch === null) {
            return;
        }
        if (firstPlayedAt === null || epoch < firstPlayedAt) {
            firstPlayedAt = epoch;
        }
        if (lastPlayedAt === null || epoch > lastPlayedAt) {
            lastPlayedAt = epoch;
        }
    });

    const windowStart = now - windowDays * MS_PER_DAY;
    const inWindow = played.filter((match) => {
        const epoch = matchEpoch(match);
        return epoch !== null && epoch >= windowStart;
    });
    const windowStats = inWindow.length ? reduceMatchStats(inWindow) : null;

    return {
        venueNames,
        matches: played,
        matchCount: played.length,
        stats,
        bestSpeedMax,
        bestDistanceTotal,
        bestSprintsTotal,
        firstPlayedAt,
        lastPlayedAt,
        windowDays,
        windowMatchCount: inWindow.length,
        windowDistanceAverage: windowStats ? windowStats.distanceAverage : 0,
    };
}
